<template>
  <div
    class="layout"
    :class="[computedCharacterColorClass]"
  >
    <!-- 梵字・家紋 + 戒名 + 位表記 -->
    <div
      class="main"
      :class="{
        adjust: isSymbolAvailable,
      }"
    >
      <!-- 梵字・家紋 -->
      <div>
        <div
          v-if="isSymbolAvailable"
          class="symbol flex center"
        >
          <div class="box">
            <ImageResolver
              :src="computedSymbolSrc"
              class="character"
            />
          </div>
        </div>
      </div>

      <!-- 戒名 + 位表記 -->
      <div class="buddhistNames">
        <div
          v-for="(column, columnIndex) in columns"
          :key="`buddhistName-${column.order}`"
          class="buddhistName flex center column"
        >
          <!-- 戒名 -->
          <TabletCharacterImage
            v-for="(character, index) in column.buddhistName"
            :key="`character-${character}-${index}`"
            :char="character"
            :font="typography.font"
            :size="computedCharacterSize"
            @onClickCharacter="onClickCharacter({
              ...buddhistNamePayload,
              characterIndex: index,
              columnIndex: columnIndex,
              ...$event,
            })"
          />

          <!-- 位表記 -->
          <TabletCharacterImage
            v-for="(character, index) in underCharacters"
            :key="`under-character-${character}-${index}`"
            :char="character"
            :font="typography.font"
            :size="computedCharacterSize"
            @onClickCharacter="onClickCharacter({
              ...underCharactersPayload,
              characterIndex: index,
              columnIndex: null,
              ...$event,
            })"
          />
        </div>
      </div>
    </div>

    <!-- 左側: 享年表記 + 没年齢 + 没年齢単位 -->
    <!-- 右側: 俗名 -->
    <div class="side-extend">
      <div />
      <div class="side-extend-wrapper">
        <!-- 左側: 享年表記 + 没年齢 + 没年齢単位 -->
        <div class="side-extend-item left flex align-center column justify-space-between">
          <div class="box grow" />
          <div class="box flex column">
            <!-- 左側: 享年表記 -->
            <TabletCharacterImage
              v-for="(character, index) in columns[0].ageAtDeathPrefix"
              :key="`ageAtDeathPrefix-${character.character}-${index}`"
              :char="character"
              :font="typography.font"
              :size="computedAgeDeathCharacterSize"
              @onClickCharacter="onClickCharacter({
                ...ageAtDeathPrefixPayload,
                characterIndex: index,
                columnIndex: 0,
                ...$event,
              })"
            />

            <!-- 左側: 没年齢 -->
            <TabletCharacterImage
              v-for="(character, index) in columns[0].ageAtDeath"
              :key="`ageAtDeath-${character.character}-${index}`"
              :char="character"
              :font="typography.font"
              :size="computedAgeDeathCharacterSize"
              @onClickCharacter="onClickCharacter({
                ...ageAtDeathPayload,
                characterIndex: index,
                columnIndex: 0,
                ...$event,
              })"
            />

            <!-- 左側: 没年齢単位 -->
            <TabletCharacterImage
              v-for="(character, index) in columns[0].ageUnitCharacter"
              :key="`ageUnitCharacter-${character.character}-${index}`"
              :char="character"
              :font="typography.font"
              :size="computedAgeDeathCharacterSize"
              @onClickCharacter="onClickCharacter({
                ...ageUnitCharacterPayload,
                characterIndex: index,
                columnIndex: 0,
                ...$event
              })"
            />
          </div>
        </div>

        <!-- 右側: 俗名 -->
        <div class="side-extend-item right flex align-center column justify-space-between">
          <div class="box grow" />
          <div
            class="secularNamePrefixes"
            :class="{
              adjust: secularNamePrefix.length,
            }"
          >
            <div
              class="secularNamePrefix flex center column"
              :class="{
                adjust: secularNamePrefix.length,
              }"
            >
              <!-- 俗名の表記（ラベル） -->
              <TabletCharacterImage
                v-for="(character, index) in secularNamePrefix"
                :key="`secularNamePrefix-${character}-${index}`"
                :char="character"
                :font="typography.font"
                :size="computedSecularNameCharacterSize"
                @onClickCharacter="onClickCharacter({
                  ...secularNamePrefixPayload,
                  characterIndex: index,
                  columnIndex: null,
                  ...$event,
                })"
              />
            </div>
          </div>

          <div class="secularNames">
            <div
              v-for="(column, columnIndex) in columns"
              :key="`secularName-${column.order}`"
              class="secularName flex center column"
            >
              <TabletCharacterImage
                v-for="(character, index) in column.secularName"
                :key="`secularName-${character}-${index}`"
                :char="character"
                :font="typography.font"
                :size="computedSecularNameCharacterSize"
                @onClickCharacter="onClickCharacter({
                  ...secularNamePayload,
                  characterIndex: index,
                  columnIndex: columnIndex,
                  ...$event,
                })"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 没年月日・没表記 -->
    <div
      class="side"
      :class="{
        adjust: isSymbolAvailable,
      }"
    >
      <div />
      <div class="side-wrapper">
        <!-- 右側: 没年月日 + 没表記 -->
        <div class="side-item right flex align-center column justify-space-between">
          <!-- 右側: 没年 -->
          <TabletCharacterImage
            v-for="(character, index) in columns[0].dateOfDeathYear"
            :key="`deathYear-${character.character}-${index}`"
            :char="character"
            :font="typography.font"
            :size="computedDateOfDeathCharacterSize"
            @onClickCharacter="onClickCharacter({
              ...dateOfDeathYearPayload,
              characterIndex: index,
              columnIndex: 0,
              ...$event,
            })"
          />

          <!-- 右側: 没月 -->
          <TabletCharacterImage
            v-for="(character, index) in columns[0].dateOfDeathMonth"
            :key="`deathMonth-${character.character}-${index}`"
            :char="character"
            :font="typography.font"
            :size="computedDateOfDeathCharacterSize"
            @onClickCharacter="onClickCharacter({
              ...dateOfDeathMonthPayload,
              characterIndex: index,
              columnIndex: 0,
              ...$event,
            })"
          />

          <!-- 右側: 没日 -->
          <TabletCharacterImage
            v-for="(character, index) in columns[0].dateOfDeathDay"
            :key="`deathDay-${character.character}-${index}`"
            :char="character"
            :font="typography.font"
            :size="computedDateOfDeathCharacterSize"
            @onClickCharacter="onClickCharacter({
              ...dateOfDeathDayPayload,
              characterIndex: index,
              columnIndex: 0,
              ...$event,
            })"
          />

          <!-- 右側: 没表記 -->
          <TabletCharacterImage
            v-for="(character, index) in diedCharacter"
            :key="`diedCharacter-${character.character}-${index}`"
            :char="character"
            :font="typography.font"
            :size="computedDateOfDeathCharacterSize"
            @onClickCharacter="onClickCharacter({
              ...diedCharacterPayload,
              characterIndex: index,
              columnIndex: null,
              ...$event,
            })"
          />
        </div>
      </div>
      <div />
    </div>

    <!-- 縁 -->
    <div class="rim" />
  </div>
</template>

<script>
import TabletPreviewMixin from "@/mixins/tabletPreview.mixin";

export default {
  mixins: [
    TabletPreviewMixin.front,
    TabletPreviewMixin.character,
  ],
  components: {
  },
  props: {
  },
  mounted () {},
  data () {
    return {
    };
  },
  computed: {
    // 戒名 + 位表記
    // の文字列の長さから要素一つあたりのサイズを算出
    computedCharacterSize () {
      const {
        isSymbolAvailable,
        columns,
        underCharacters,
      } = this;

      const defaultSize = 45;
      const maxHeight = !isSymbolAvailable ? 400 - 15 - 15 - 30 : 320 - 15 - 30;

      const characterLength = columns[0].buddhistName.length + underCharacters.length;
      const characterSize = maxHeight / characterLength;
      const size = Math.min(defaultSize, characterSize);

      return size;
    },

    // 俗名の表記（ラベル）+ 俗名
    // の文字列の長さから要素一つあたりのサイズを算出
    computedSecularNameCharacterSize () {
      const {
        isSymbolAvailable,
        columns,
        secularNamePrefix,
      } = this;

      const defaultSize = 20;
      const gap = secularNamePrefix.length ? 45 : 30;
      // const maxHeight = 320 - 15 - 30 - 30 - gap;
      const maxHeight = !isSymbolAvailable ? 400 - 30 - 30 - gap : 320 - 15 - 30 - gap;

      const characterLength = columns[0].secularName.length + secularNamePrefix.length;
      const characterSize = maxHeight / characterLength;
      const size = Math.min(defaultSize, characterSize);

      return size;
    },

    // 享年表記 + 没年齢 + 没年齢単位
    // の文字列の長さから要素一つあたりのサイズを算出
    computedAgeDeathCharacterSize () {
      const { isSymbolAvailable } = this;
      const {
        ageAtDeathPrefix,
        ageAtDeath,
        ageUnitCharacter,
      } = this.columns[0];

      const defaultSize = 15;
      // const maxHeight = 400 - 15 - 15 - 30; // padding の上下分 + rim 分も追加
      const maxHeight = !isSymbolAvailable ? 400 - 15 - 15 - 30 : 320 - 15 - 30;

      const ageAtDeathPrefixLength = ageAtDeathPrefix ? ageAtDeathPrefix.length : 0;
      const ageUnitCharacterLength = ageUnitCharacter ? ageUnitCharacter.length : 0;
      const characterLength = ageAtDeathPrefixLength + ageAtDeath.length + ageUnitCharacterLength;
      const characterSize = maxHeight / characterLength;
      const size = Math.min(defaultSize, characterSize);

      return size;
    },

    // 没年, 没月, 没日, 没表記
    // の文字列の長さから要素一つあたりのサイズを算出
    computedDateOfDeathCharacterSize () {
      const {
        isSymbolAvailable,
        diedCharacter,
      } = this;
      const {
        dateOfDeathYear,
        dateOfDeathMonth,
        dateOfDeathDay,
      } = this.columns[0];

      const defaultSize = 20;
      // const maxHeight = 400 - 15 - 15 - 30; // .right の padding の上下分 + rim 分も追加
      const maxHeight = !isSymbolAvailable ? 400 - 15 - 15 - 30 : 320 - 15 - 30;

      const characterLength = dateOfDeathYear.length + dateOfDeathMonth.length + dateOfDeathDay.length + diedCharacter.length;
      const characterSize = maxHeight / characterLength;
      const size = Math.min(defaultSize, characterSize);

      return size;
    },
  },
  watch: {},
  destroyed () {},
  methods: {},
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/variables.scss";
@import "~@/assets/styles/tablet-front.scss";

.layout {
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  .main {
    display: grid;
    grid-template-rows: auto 1fr;
    grid-template-columns: 1fr;
    height: 100%;
    width: 50px;
    position: absolute;
    top: 0;
    left: -50px;
    box-sizing: border-box;
    padding-top: 15px; // 15(= rim)
    padding-bottom: 15px; // 15(= rim)
    overflow-x: visible;
    z-index: 1;

    &.adjust {
      padding-top: 15px; // 15(= rim)
      padding-bottom: 30px; // (80 - 50) / 2 + 15(= rim)

      .buddhistNames {
        padding: 0;
      }
    }

    @include symbol(175px);  // (300 + 50) / 2

    .buddhistNames {
      height: 100%;
      width: 100%;
      position: relative;
      padding: 15px 0;
      box-sizing: border-box;

      .buddhistName {
        height: 100%;
        transform: translateX(175px); // translateX(111px);

        @include character(45px);
      }
    }
  }

  .side-extend {
    display: grid;
    grid-template-rows: 1fr auto;
    grid-template-columns: 1fr;
    height: 100%;
    position: absolute;
    top: 0;
    left: -100px;
    padding-top: 30px;
    padding-bottom: 30px;
    box-sizing: border-box;
    z-index: 2;

    &-wrapper {
      height: 100%;
      width: 100%;
      position: relative;
    }

    &-item {
      height: 100%;
      width: 25px;

      &.left {
        float: left;
        transform: translateX(150px);
      }

      &.right {
        float: right;
        transform: translate(155px, -15px);

        .secularNamePrefixes {
          padding-top: 15px;

          &.adjust {
            padding-bottom: 15px;
          }
        }
      }

      @include character(20px);
    }
  }

  .side {
    display: grid;
    grid-template-rows: 1fr auto 1fr;
    grid-template-columns: 1fr;
    height: 100%;
    position: absolute;
    top: 0;
    left: -100px;
    padding-top: 15px;
    padding-bottom: 15px;
    box-sizing: border-box;
    z-index: 3;

    &.adjust {
      grid-template-rows: 1fr auto 1fr;
      height: calc(100% - 80px);
      top: 80px;
      padding-bottom: 30px;
    }

    &-wrapper {
      height: 100%;
      width: 100%;
      position: relative;
    }

    &-item {
      height: 100%;
      width: 50px;

      &.left {
        float: left;
      }

      &.right {
        float: right;
        transform: translateX(280px);
      }

      @include character(20px);
    }
  }

  .rim {
    display: block;
    height: 376px;
    width: 276px;
    margin: 12px;
    position: relative;
    box-sizing: border-box;
    border: 3px solid white;
  }
}
</style>
